/* Testimonials — swipe carousel + dot pagination + auto-scroll
 *
 * - Manual horizontal swipe remains enabled.
 * - Dot pagination remains enabled.
 * - Automatically advances every 5 seconds.
 * - Loops back to the first card after the last.
 * - Pauses auto-scroll while the user is interacting with the carousel.
 */

(() => {
  const setup = (section) => {
    if (section.dataset.ckSlider === 'ready') return;

    const viewport = section.querySelector('.testimonials__marquee');
    const track = section.querySelector('.testimonials__track');
    const dotsWrap = section.querySelector(
      '[data-testimonials-dots], .testimonials__pagination'
    );

    if (!viewport || !track) return;

    const group =
      track.querySelector("[data-ck-marquee-source='true']") ||
      track.querySelector('.ck-marquee-group') ||
      track.querySelector('.testimonials__group');

    if (!group) return;

    // Original cards only — exclude marquee fill copies.
    const cards = [...group.children].filter(
      (el) =>
        el.classList.contains('testimonial-card') &&
        el.dataset.ckFillCopy !== 'true'
    );

    if (cards.length < 1) return;

    // Undo the marquee.
    track
      .querySelectorAll("[data-ck-marquee-copy='true']")
      .forEach((n) => n.remove());

    group
      .querySelectorAll("[data-ck-fill-copy='true']")
      .forEach((n) => n.remove());

    group.removeAttribute('data-ck-marquee-source');

    track.classList.remove(
      'ck-marquee-track',
      'ck-seamless-marquee'
    );

    viewport.classList.remove('ck-marquee-viewport');

    // Force normal horizontal slider behavior.
    viewport.style.overflowX = 'auto';
    viewport.style.overflowY = 'hidden';
    viewport.style.setProperty(
      '-webkit-overflow-scrolling',
      'touch'
    );
    viewport.style.scrollSnapType = 'x mandatory';

    track.style.animation = 'none';
    track.style.transform = 'none';
    track.style.width = 'max-content';

    section.dataset.ckSlider = 'ready';

    // Build dots.
    let dots = [];

    if (dotsWrap) {
      dotsWrap.innerHTML = cards.map(() => '<i></i>').join('');
      dots = [...dotsWrap.querySelectorAll('i')];
    }

    if (!dots.length) return;

    // Calculate distance between cards.
    const step = () => {
      if (cards.length < 2) {
        return viewport.clientWidth || 1;
      }

      const distance =
        cards[1].getBoundingClientRect().left -
        cards[0].getBoundingClientRect().left;

      return distance > 0
        ? distance
        : viewport.clientWidth || 1;
    };

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    };

    // Dots follow the positions the strip can actually stop at. With several
    // cards in view that is fewer than the cards, so the spare dots are hidden
    // and the last dot lights up when the strip is scrolled to its end.
    const stops = () => {
      const max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 2) return 1;
      // Rounded up: a final, shorter step to the end is still a position.
      return Math.min(dots.length, Math.ceil(max / step() - 0.05) + 1);
    };

    const layoutDots = () => {
      const count = stops();
      dots.forEach((dot, i) => {
        dot.style.display = count > 1 && i < count ? '' : 'none';
      });
      return count;
    };

    const activeIndex = () => {
      const count = layoutDots();
      const max = viewport.scrollWidth - viewport.clientWidth;
      if (max > 2 && viewport.scrollLeft >= max - 2) return count - 1;
      return Math.min(count - 1, Math.max(0, Math.round(viewport.scrollLeft / step())));
    };

    let frame = 0;

    window.addEventListener('resize', () => setActive(activeIndex()));

    // Keep dots synchronized with manual swiping.
    viewport.addEventListener(
      'scroll',
      () => {
        if (frame) return;

        frame = requestAnimationFrame(() => {
          frame = 0;

          setActive(activeIndex());
        });
      },
      { passive: true }
    );

    // Dot navigation.
    dots.forEach((dot, i) => {
      dot.style.cursor = 'pointer';

      dot.addEventListener('click', () => {
        setActive(i);

        viewport.scrollTo({
          left: i * step(),
          behavior: 'smooth'
        });

        // Restart the 5-second timer after manual navigation.
        resetAutoScroll();
      });
    });

    // ----------------------------------------
    // AUTO SCROLL
    // ----------------------------------------

    const AUTO_SCROLL_DELAY = 5000;

    let autoScrollTimer = null;

    const getCurrentIndex = () => activeIndex();

    const autoScroll = () => {
      const currentIndex = getCurrentIndex();

      // Go to next card, or back to first card.
      // Wrap after the last position the strip can reach, not the last card.
      const nextIndex =
        currentIndex >= stops() - 1
          ? 0
          : currentIndex + 1;

      setActive(nextIndex);

      viewport.scrollTo({
        left: nextIndex * step(),
        behavior: 'smooth'
      });
    };

    const startAutoScroll = () => {
      clearInterval(autoScrollTimer);

      autoScrollTimer = setInterval(
        autoScroll,
        AUTO_SCROLL_DELAY
      );
    };

    const resetAutoScroll = () => {
      startAutoScroll();
    };

    // Pause while user is touching/swiping.
    viewport.addEventListener(
      'touchstart',
      () => {
        clearInterval(autoScrollTimer);
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        resetAutoScroll();
      },
      { passive: true }
    );

    // Pause while mouse is over the carousel.
    viewport.addEventListener('mouseenter', () => {
      clearInterval(autoScrollTimer);
    });

    viewport.addEventListener('mouseleave', () => {
      resetAutoScroll();
    });

    // ----------------------------------------
    // MOUSE DRAG
    // ----------------------------------------
    // A scrolling strip moves with touch, a trackpad or the wheel, but not
    // with a mouse press-and-drag, so that is added here. Snapping is switched
    // off while dragging (it would fight the pointer) and restored on release,
    // which settles the strip on the nearest card. A drag that actually moved
    // also swallows the click that follows, so letting go over a card does not
    // count as clicking it.
    let dragging = false;
    let dragMoved = false;
    let dragStartX = 0;
    let dragStartLeft = 0;

    viewport.style.cursor = 'grab';

    viewport.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      dragging = true;
      dragMoved = false;
      dragStartX = event.clientX;
      dragStartLeft = viewport.scrollLeft;
      clearInterval(autoScrollTimer);
    });

    window.addEventListener('mousemove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(delta) < 5) return;
      if (!dragMoved) {
        dragMoved = true;
        viewport.style.scrollSnapType = 'none';
        viewport.style.cursor = 'grabbing';
        viewport.style.userSelect = 'none';
      }
      event.preventDefault();
      viewport.scrollLeft = dragStartLeft - delta;
    });

    window.addEventListener('mouseup', () => {
      if (!dragging) return;
      dragging = false;
      viewport.style.cursor = 'grab';
      viewport.style.userSelect = '';
      if (dragMoved) {
        // Glide to the nearest card, then hand snapping back to the browser.
        const target = Math.round(viewport.scrollLeft / step()) * step();
        viewport.scrollTo({ left: target, behavior: 'smooth' });
        setTimeout(() => {
          viewport.style.scrollSnapType = 'x mandatory';
        }, 450);
      }
      resetAutoScroll();
    });

    viewport.addEventListener(
      'click',
      (event) => {
        if (!dragMoved) return;
        event.preventDefault();
        event.stopPropagation();
        dragMoved = false;
      },
      true
    );

    // Images would otherwise start a native drag-and-drop instead.
    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    // Start from first testimonial.
    viewport.scrollLeft = 0;
    setActive(activeIndex());

    // Start 5-second auto-scroll.
    startAutoScroll();
  };

  const run = () => {
    document
      .querySelectorAll('.testimonials')
      .forEach(setup);
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run);
  }

  // site-experience.js prepares the marquee after load.
  setTimeout(run, 500);
})();